import { defineStore } from 'pinia'
import { ref } from 'vue'
import { platformPath } from '@remote/platform'

interface EnvCollection {
  justOne?: '0' | '1'
  nickname?: string
  noActionBar?: 'true' | 'false'
  noStatusBar?: 'true' | 'false'
  platform?: Platform
  plm?: PlatformShort
  privilege?: string
  runner_space?: string
  space?: string
  user_id?: string
}



/**
 * 页面运行环境变量相关的store
 * 这里面的变量是从页面打开的url，或者是调服务端的 /device/control 接口获取的
 * 请注意，页面状态不应该写入到这个store里面。
 */
export const useEnvStore = defineStore('env', () => {
  /**
   * 平台名称缩写
   */
  const plm = ref<PlatformShort>('')
  /**
   * 平台名称
   */
  const platform = ref<Platform>('')
  /**
   * 设备id，和 /watch 接口中返回的 target 是同一个东西
   */
  const space = ref('')
  /**
   * 设备空间
   */
  const runnerSpace = ref('')
  /**
   * 设备列表是否只展示当前设备
   */
  const justOne = ref<'0' | '1'>('1')
  /**
   * 当前设备登录的迅雷账号用户名
   */
  const nickname = ref('')
  /**
   * 当前设备登录的迅雷账号userID
   */
  const userID = ref('')
  /**
   * 使用特权，用来分平台，标记用户是否能够使用NAS迅雷。
   */
  const privilege = ref('')
  /**
   * 是否隐藏ActionBar
   * ActionBar是状态栏下方的一个横栏，通常展示页面title和返回按钮
   */
  const noActionBar = ref<'true' | 'false'>('false')
  /**
   * 是否隐藏状态栏
   * StatusBar是展示电量信息、时间等的那一栏。
   */
  const noStatusBar = ref<'true' | 'false'>('false')
  
  /**
   * 路径环境。
   * 不同厂商的代码部署在不同路径下。
   * e.g. 访问华为的实例  https://pan.xunlei.com/yc/huawei - pathEnv: huawei
   * e.g. 访问极空间的实例  https://pan.xunlei.com/yc/jkj - pathEnv: jkj
   */
  const pathEnv = ref<PathEnv>(platformPath)

  /**
   * 设备超出数量限制弹窗是否已经弹出过。
   * 每次getRunner都会判断一次设备是否超出数量限制，如果弹出过则不应该再弹出
   */
  const hasPopDeviceLimitExceededShown = ref(false)

  /**
   * 是否在PC迅雷内的远程管理入口打开
   */
  const isInPC = ref(window.navigator.userAgent.includes('TBC'))

  /**
   * 更新环境变量
   * 因为代码里统一用驼峰，但入参的字段名风格不一，这里做下转换
   */
  const updateEnv = (props: EnvCollection) => {
    if (props.plm) plm.value = props.plm
    if (props.platform) platform.value = props.platform
    if (props.justOne) justOne.value = props.justOne
    if (props.nickname) nickname.value = props.nickname
    if (props.noActionBar) noActionBar.value = props.noActionBar
    if (props.noStatusBar) noStatusBar.value = props.noStatusBar
    if (props.privilege) privilege.value = props.privilege
    if (props.runner_space) runnerSpace.value = props.runner_space
    if (props.space) space.value = props.space
    if (props.user_id) userID.value = props.user_id
  }

  return {
    justOne,
    nickname,
    noActionBar,
    noStatusBar,
    platform,
    plm,
    privilege,
    space,
    runnerSpace,
    userID,
    pathEnv,
    hasPopDeviceLimitExceededShown,
    isInPC,
    updateEnv,
  }

})